import * as React from "react";
import styled from "styled-components";

import {routeConfig} from "../../config/language/ja-JP/presentation/route_text";
import {ConstBreakPoint} from "../../config/styles_const/styles_const";
import {Customer} from "../../domain/model/customer";
import {ICustomerRepository} from "../../domain/repository/customer_repository";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {createCustomerAPI} from "../../infra/api/service/customer_api";
import ScoutCustomerCard from "../common/organism/card/scout_customer_card";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import EmptyData from "../common/organism/empty_data";
import {TBreadcrumbItem} from "../type/common";

// 検索のサンプルクエリ（主部のみ。表示時に「を探しています」を付与する）
const exampleQueries: string[] = [
    "上場企業でM&AとPMIを複数回リードした元経営企画責任者",
    "新規事業の立上げ〜グロースを経験した元プロダクト責任者",
];

// プログレスバーが99%に到達するまでの最大時間（ミリ秒）
const PROGRESS_DURATION_MS: number = 20000;

// 進捗(%)に応じてプログレス下に表示するメッセージ
const progressMessages: Array<{ threshold: number; message: string }> = [
    {threshold: 80, message: "お待たせしました！もうすぐです！🚀"},
    {threshold: 70, message: "AIエージェントは最終調整を行なっています。"},
    {threshold: 60, message: "最適な順番に並べ替えてみます。"},
    {threshold: 50, message: "より最適なエキスパートが見つかりました！"},
    {threshold: 40, message: "さらに深く調査してみます👓"},
    {threshold: 30, message: "候補者が見つかりました！👍"},
    {threshold: 20, message: "全体の中から候補者を選定しています"},
    {threshold: 10, message: "最適なエキスパートを見つけてきます！"},
    {threshold: 0, message: "検索してくれてありがとうございます✨"},
];

const getProgressMessage = (progress: number): string => {
    const found = progressMessages.find((item) => progress >= item.threshold);
    return found ? found.message : progressMessages[progressMessages.length - 1].message;
};

const apiClient: IApiClient = createApiClient();
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);

interface IProps {
}

const ClientAgenticSearch: React.FC<IProps> = (props) => {
    const [query, setQuery] = React.useState<string>("");
    const [submittedQuery, setSubmittedQuery] = React.useState<string>("");
    const [customers, setCustomers] = React.useState<Customer[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [hasSearched, setHasSearched] = React.useState<boolean>(false);
    const [progress, setProgress] = React.useState<number>(0);

    // ローディング中は最大20秒かけてぬるぬるとプログレスを進める。
    // 20秒以上経っても検索結果が来ない場合は99%で止める。
    React.useEffect(() => {
        if (!isLoading) {
            return;
        }

        setProgress(0);
        const startedAt: number = Date.now();
        const timer: number = window.setInterval(() => {
            const elapsed: number = Date.now() - startedAt;
            const ratio: number = Math.min(elapsed / PROGRESS_DURATION_MS, 1);
            // ease-outで徐々に減速させながら進める
            const eased: number = 1 - Math.pow(1 - ratio, 2.2);
            // 99%を上限とする（結果が来るまで100%にはしない）
            setProgress(Math.min(99, eased * 99));
            if (ratio >= 1) {
                window.clearInterval(timer);
            }
        }, 50);

        return () => {
            window.clearInterval(timer);
        };
    }, [isLoading]);

    const handleSearch = (searchQuery: string): void => {
        const trimmed: string = searchQuery.trim();
        if (!trimmed || isLoading) {
            return;
        }

        setIsLoading(true);
        setHasSearched(true);
        setSubmittedQuery(trimmed);

        customerRepository
            .agenticSearch(trimmed)
            .then((items: Customer[]) => {
                setCustomers(items);
            })
            .catch(() => {
                setCustomers([]);
            })
            .then(() => {
                setIsLoading(false);
            });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        handleSearch(query);
    };

    const breadcrumbItems: TBreadcrumbItem[] = [
        routeConfig.clientRoot,
        {text: `AIエージェント検索`},
    ];

    return (
        <React.Fragment>
            <PageWrapper>
                <Breadcrumbs breadcrumbItems={breadcrumbItems}/>

                <SearchSection searched={hasSearched}>
                    <PreSearchHeading searched={hasSearched}>
                        どのようなエキスパートをお探しですか？
                    </PreSearchHeading>
                    <SearchForm onSubmit={handleSubmit}>
                        <SearchInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={"エキスパートに求める要件などを記載してください"}
                        />
                        <SearchButton type="submit" disabled={isLoading || !query.trim()}>
                            <SearchButtonIcon icon={"'\f062'"}/>
                        </SearchButton>
                    </SearchForm>

                    <ExampleList>
                        {exampleQueries.map((example, i) => {
                            return (
                                <ExampleItem key={i}>
                                    <ExampleSubject>{example}</ExampleSubject>
                                    <ExampleSuffix>を探しています</ExampleSuffix>
                                </ExampleItem>
                            );
                        })}
                    </ExampleList>
                </SearchSection>

                {hasSearched && (
                    <ResultSection>
                        {!isLoading && (
                            <ResultHeadline>AIエージェントからの回答結果</ResultHeadline>
                        )}

                        {isLoading ? (
                            <LoadingArea>
                                <LoadingTitle>
                                    👩‍💻AIエージェントが検索しています
                                </LoadingTitle>
                                <ProgressBarRow>
                                    <ProgressBarTrack>
                                        <ProgressBarFill style={{width: `${progress}%`}}/>
                                    </ProgressBarTrack>
                                    <ProgressPercent>{Math.floor(progress)}%</ProgressPercent>
                                </ProgressBarRow>
                                <LoadingSubText>
                                    {getProgressMessage(progress)}
                                </LoadingSubText>
                            </LoadingArea>
                        ) : customers.length > 0 ? (
                            <React.Fragment>
                                <ResultSummary>
                                    「{submittedQuery}」に関して最適なエキスパートを
                                    {customers.length}名ご紹介します。各カード上部のAIによる要約をご確認ください。
                                </ResultSummary>

                                <CardList>
                                    {customers.map((customer: Customer) => {
                                        return (
                                            <CardItem key={customer.id}>
                                                {customer.agenticSummary && (
                                                    <AgenticSummary>
                                                        {customer.agenticSummary}
                                                    </AgenticSummary>
                                                )}
                                                <ScoutCustomerCard
                                                    customer={customer}
                                                    jobCareers={[]}
                                                    isBookmarked={false}
                                                    cardURL={`/client/scout/customer/${customer.id}`}
                                                    openInNewTab={true}
                                                />
                                            </CardItem>
                                        );
                                    })}
                                </CardList>
                            </React.Fragment>
                        ) : (
                            <EmptyData
                                main={"該当するエキスパートが見つかりませんでした"}
                                description={"検索条件を変えて、再度お試しください。"}
                            />
                        )}
                    </ResultSection>
                )}
            </PageWrapper>
        </React.Fragment>
    );
};

export default ClientAgenticSearch;

const SearchSection = styled.div<{ searched: boolean }>`
    width: 80%;
    max-width: 860px;
    margin-left: auto;
    margin-right: auto;
    // 検索前は中央寄りに下げ、検索後は上部(20px)へアニメーションで移動する
    margin-top: ${({searched}) => (searched ? "20px" : "150px")};
    transition: margin-top 0.5s ease;
`;

const PreSearchHeading = styled.div<{ searched: boolean }>`
    text-align: center;
    font-weight: bold;
    color: #1a1a1a;
    overflow: hidden;
    // 検索後はフェードアウトしながら高さを潰して消す
    opacity: ${({searched}) => (searched ? 0 : 1)};
    max-height: ${({searched}) => (searched ? "0" : "60px")};
    margin-bottom: ${({searched}) => (searched ? "0" : "40px")};
    font-size: 24px;
    transition: opacity 0.4s ease, max-height 0.4s ease, margin-bottom 0.4s ease;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        font-size: 28px;
    }
`;

const SearchForm = styled.form`
    position: relative;
    width: 100%;
    border: 1px solid #e8e8e8;
    border-radius: 24px;
    padding: 28px 28px 72px;
    background-color: #fff;
    box-shadow: 0px 2px 10px rgba(199, 199, 199, 0.25);
    margin-top: 20px;
`;

const SearchInput = styled.textarea`
    display: block;
    width: 100%;
    min-height: 56px;
    border: none;
    outline: none;
    resize: none;
    font-size: 18px;
    line-height: 1.7;
    color: #333;
    background-color: transparent;
    font-family: inherit;

    &::placeholder {
        color: #b5b5b5;
    }
`;

const SearchButton = styled.button`
    position: absolute;
    right: 20px;
    bottom: 20px;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background-color: #1a1a1a;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }
`;

const SearchButtonIcon = styled.span<{ icon: string }>`
    &::before {
        font-family: "Font Awesome 5 Free";
        font-weight: 900;
        content: ${({icon}) => icon};
        font-size: 16px;
    }
`;

const ExampleList = styled.div`
    margin-top: 32px;
`;

const ExampleItem = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    font-size: 12px;
    line-height: 1.6;
    padding: 18px 4px;
    border-bottom: 1px solid #eee;
`;

const ExampleSubject = styled.span`
    color: #1a1a1a;
`;

const ExampleSuffix = styled.span`
    color: #c8c8c8;
`;

const ResultSection = styled.div`
    margin-top: 40px;
`;

const ResultHeadline = styled.div`
    font-weight: bold;
    font-size: 16px;
    color: #333;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        font-size: 18px;
    }
`;

const LoadingArea = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 0;
`;

const LoadingTitle = styled.div`
    font-weight: bold;
    font-size: 18px;
    color: #1a1a1a;
    text-align: center;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        font-size: 22px;
    }
`;

const ProgressBarRow = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 760px;
    margin-top: 28px;
`;

const ProgressBarTrack = styled.div`
    flex: 1;
    height: 8px;
    border-radius: 999px;
    background-color: #eef0f3;
    overflow: hidden;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #6b8cff 0%, #4a6cf7 100%);
    transition: width 0.1s linear;
`;

const ProgressPercent = styled.div`
    margin-left: 16px;
    min-width: 40px;
    text-align: right;
    font-size: 13px;
    color: #9aa0a6;
`;

const LoadingSubText = styled.div`
    margin-top: 18px;
    font-size: 14px;
    color: #c0c4cc;
    text-align: center;
`;

const ResultSummary = styled.div`
    font-size: 12px;
    line-height: 24px;
    color: #333;
    margin-top: 16px;
    white-space: pre-wrap;
`;

const CardList = styled.div`
    margin-top: 24px;
    display: grid;
    grid-row-gap: 55px;
`;

const CardItem = styled.div`
    display: grid;
    grid-row-gap: 10px;
`;

// カードの外側・上に表示するLLMによる要約
const AgenticSummary = styled.div`
    font-size: 13px;
    line-height: 1.8;
    color: #555;
    padding-left: 4px;
    white-space: pre-wrap;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/client_agentic_search.tsx